import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MapPin, Building2, DollarSign, Star, Code, Users, MessageSquare } from 'lucide-react'
import { useDevelopers } from '@/hooks/developers'
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@/components/ui'
import { UserAvatar } from '@/components/UserAvatar'

export default function Developers() {
  const { data: developers, isLoading, error } = useDevelopers()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/50">
        <div className="container relative mx-auto px-6 py-16">
          <div className="flex h-64 items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary/20 border-t-primary"></div>
                <div
                  className="absolute inset-0 h-12 w-12 animate-spin rounded-full border-4 border-transparent border-t-primary/40"
                  style={{
                    animationDirection: 'reverse',
                    animationDuration: '1.5s',
                  }}
                ></div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-foreground">Finding amazing developers</h3>
                <p className="text-muted-foreground">Just a moment...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/50">
        <div className="container relative mx-auto px-6 py-16">
          <Card className="mx-auto max-w-md border-destructive/20 bg-destructive/5">
            <CardContent className="p-8 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <Users className="h-6 w-6 text-destructive" />
              </div>
              <h3 className="mb-2 text-lg font-medium text-foreground">
                Failed to load developers
              </h3>
              <p className="mb-4 text-muted-foreground">
                We couldn't fetch the developer list. Please try again.
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-muted/20">
        <div className="container relative mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mx-auto max-w-3xl space-y-6 text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary"
            >
              <Code className="h-4 w-4" />
              Discover Talent
            </motion.div>

            <h1 className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-4xl font-bold text-transparent md:text-6xl">
              Available Developers
            </h1>

            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              Connect with skilled developers ready to bring your projects to life.
              <span className="font-semibold text-primary"> Find your perfect match.</span>
            </p>

            {developers && developers.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="flex items-center justify-center gap-8 text-sm text-muted-foreground"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span>{developers.length} Developers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary" />
                  <span>Verified Profiles</span>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Developers Grid */}
      <section className="bg-background py-16">
        <div className="container mx-auto px-6">
          {developers && developers.length > 0 ? (
            <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 lg:grid-cols-3">
              {developers.map((developer, index) => (
                <motion.div
                  key={developer.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="group border-border/50 bg-card backdrop-blur-sm transition-all duration-300 hover:border-primary/20 hover:shadow-xl hover:shadow-primary/5">
                    <CardHeader className="pb-3">
                      <div className="flex items-start gap-4">
                        <div className="relative">
                          <UserAvatar
                            size="md"
                            avatarUrl={developer.avatar_url}
                            name={developer.username}
                            className="border border-border/50"
                          />
                          <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-background bg-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-lg font-semibold text-foreground transition-colors group-hover:text-primary">
                            {developer.username}
                          </CardTitle>
                          {developer.hourly_rate && (
                            <div className="mt-1 flex items-center gap-1">
                              <DollarSign className="h-3 w-3 text-primary" />
                              <span className="text-sm font-medium text-primary">
                                ${developer.hourly_rate}/hour
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      {developer.bio && (
                        <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                          {developer.bio}
                        </p>
                      )}

                      <div className="space-y-2">
                        {developer.company && (
                          <div className="flex items-center gap-2 text-sm">
                            <Building2 className="h-3 w-3 text-muted-foreground" />
                            <span className="font-medium text-foreground">{developer.company}</span>
                          </div>
                        )}
                        {developer.location && (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">{developer.location}</span>
                          </div>
                        )}
                      </div>

                      {developer.skills && developer.skills.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Expertise
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {developer.skills.slice(0, 4).map((skill) => (
                              <Badge
                                key={skill.id}
                                variant="secondary"
                                className="border-primary/20 bg-primary/10 text-xs text-primary transition-colors hover:bg-primary/20"
                              >
                                {skill.name}
                              </Badge>
                            ))}
                            {developer.skills.length > 4 && (
                              <Badge
                                variant="outline"
                                className="border-muted-foreground/30 text-xs text-muted-foreground"
                              >
                                +{developer.skills.length - 4} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <Button
                          asChild
                          size="sm"
                          variant="outline"
                          className="flex-1 border-primary/20"
                        >
                          <Link to={`/messages?user=${developer.username}`}>
                            <MessageSquare className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          asChild
                          size="sm"
                          className="flex-[2] border border-primary/20 bg-primary/10 text-primary transition-all hover:bg-primary hover:text-primary-foreground"
                          variant="outline"
                        >
                          <Link to={`/profiles/${developer.username}`}>View Profile</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto max-w-md"
            >
              <Card className="border-border/50 bg-card backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted/50">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="mb-2 text-lg font-medium text-foreground">No developers yet</h3>
                  <p className="mb-6 text-muted-foreground">
                    We're working on building our developer community. Check back soon!
                  </p>
                  <Button
                    variant="outline"
                    className="border-primary/30 text-primary hover:bg-primary/10"
                  >
                    Join as Developer
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  )
}
